import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FileDown, Trash2, Search, BadgeEuro } from "lucide-react";
import { api, downloadPdf, errMsg, eur, fmtDate } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, Empty, PageHead, Pill, StatCard } from "@/components/common";

export default function Fatture() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const load = useCallback(() => { api.get("/admin/fatture").then((r) => setItems(r.data)); }, []);
  useEffect(() => { load(); }, [load]);
  const rows = useMemo(() => items.filter((f) => !q || [f.numero, f.cliente_nome, f.ddt_numero, f.cliente_piva].some((v) => String(v || "").toLowerCase().includes(q.toLowerCase()))), [items, q]);
  const tot = rows.reduce((a, f) => a + f.totale, 0);
  const daPagare = rows.filter((f) => !f.pagata).reduce((a, f) => a + f.totale, 0);

  const pdf = (f) => downloadPdf(`/admin/fatture/${f.id}/pdf`, `Fattura_${f.numero.replace("/", "-")}.pdf`).catch((e) => toast.error(errMsg(e)));
  const del = async (f) => { if (window.confirm(`Eliminare la fattura n. ${f.numero}?`)) { await api.delete(`/admin/fatture/${f.id}`); load(); } };
  const togglePaid = async (f) => { try { await api.put(`/admin/fatture/${f.id}`, { ...f, pagata: !f.pagata }); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div data-testid="admin-fatture">
      <PageHead eyebrow="Fatturazione" title="Fatture" subtitle="Generate dai DDT con il tasto “Fattura”. La numerazione prosegue dal numero impostato in Impostazioni." />
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <StatCard label="Fatture" value={rows.length} testid="fatture-count" />
        <StatCard label="Totale fatturato" value={eur(tot)} testid="fatture-total" />
        <StatCard label="Da incassare" value={eur(daPagare)} tone="gold" testid="fatture-unpaid" />
      </div>
      <div className="relative max-w-sm mb-6">
        <Search className="h-4 w-4 absolute left-3 top-3 text-[#5C6B73]" />
        <Input placeholder="Cerca numero, cliente, DDT…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-white" data-testid="fatture-search" />
      </div>
      {rows.length === 0 ? <Empty>Nessuna fattura. Vai in Archivio DDT e premi “Fattura” su un documento.</Empty> : (
        <Card className="overflow-x-auto">
          <Table data-testid="fatture-table">
            <TableHeader><TableRow><TableHead>N°</TableHead><TableHead>Data</TableHead><TableHead>Cliente</TableHead><TableHead>Rif. DDT</TableHead><TableHead className="text-right">Imponibile</TableHead><TableHead className="text-right">IVA</TableHead><TableHead className="text-right">Totale</TableHead><TableHead>Stato</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {rows.map((f) => (
                <TableRow key={f.id} data-testid={`fattura-row-${f.id}`}>
                  <TableCell className="font-display text-lg font-semibold text-[#0D3B52]">{f.numero}</TableCell>
                  <TableCell className="whitespace-nowrap">{fmtDate(f.data)}</TableCell>
                  <TableCell><p className="font-semibold">{f.cliente_nome}</p><p className="text-xs text-[#5C6B73]">{f.cliente_piva}</p></TableCell>
                  <TableCell>{f.ddt_numero || "—"}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">{eur(f.imponibile)}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">{eur(f.iva_importo)}</TableCell>
                  <TableCell className="text-right whitespace-nowrap font-semibold">{eur(f.totale)}</TableCell>
                  <TableCell><button type="button" onClick={() => togglePaid(f)} data-testid={`fattura-paid-${f.id}`}><Pill cls={f.pagata ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}><BadgeEuro className="h-3 w-3 mr-1" />{f.pagata ? "Pagata" : "Da pagare"}</Pill></button></TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    <Button size="icon" variant="ghost" onClick={() => pdf(f)} data-testid={`fattura-pdf-${f.id}`}><FileDown className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(f)} data-testid={`fattura-delete-${f.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
